import React, { FC, useEffect, useMemo, useState } from "react";
import CampaignDetailsComponent from "components/BundlePromotion/CampaignDetails";
import { useDispatch, useSelector } from "react-redux";
import { IState, ECampaignActions, ECampaignStatus } from "store/store.interface";
import FormLoader from "Loader/Form";
import { setLocation } from "store/actions/auth.actions";
import Layout from "Layout";
import { useApiInfo, usePermissions } from "hooks";
import {
  BUNDLE_PROMOTION_GET_CAMPAIGN_BY_ID,
  BUNDLE_PROMOTION_SET_VALUE,
  BUNDLE_PROMOTION_UPDATE_STATUS,
  BUNDLE_PROMOTION_CREATE
} from "store/store.types";
import {
  clearCreationBundleForm,
  setBundlePromotionType,
  updateBundleCampaignStatus
} from "store/actions/bundlePromotion.actions";
import Section from "Layout/Section";
import { Box, Card, Link, Button } from "common";
import { ERoles } from "store/reducers/auth.reducers";
import { normalizeBundleFormData } from '../../components/BundlePromotion/BundleHelpers'
import { clearApi } from "store/actions/api.actions";

const CampaignDetails: FC = () => {
  const { roles, bundleCampaign } = useSelector((state: IState) => ({ ...state.bundlePromotion, ...state.auth, ...state.config }));
  const [loadingState, setLoadingState] = useState("");
  const { isLoading, error, done } = useApiInfo(BUNDLE_PROMOTION_UPDATE_STATUS);

  const { isLoading: isGettingCampaignLoading, done: isGettingCampaignDone } =
    useApiInfo(BUNDLE_PROMOTION_GET_CAMPAIGN_BY_ID);

  const dispatch = useDispatch();
  let status = bundleCampaign?.status?.toLowerCase() as ECampaignStatus;

  useEffect(() => {
    if (!isLoading && (done || error)) {
      setLoadingState("");
    }
  }, [isLoading, error, done, dispatch]);

  const availableUserActions = usePermissions(
    bundleCampaign ? status : undefined
  ).filter((action) => !["Get Report", "Download Coupons"].includes(action.title));

  const onApprove = () => {
    const { campaignId } = bundleCampaign || {};
    if (campaignId && !isLoading) {
      setLoadingState(ECampaignActions.approve);
      dispatch(updateBundleCampaignStatus(campaignId, ECampaignStatus.approved));
    }
  };

  const onComplete = () => {
    const { campaignId } = bundleCampaign || {};
    if (campaignId && !isLoading) {
      setLoadingState(ECampaignActions.complete);
      dispatch(updateBundleCampaignStatus(campaignId, ECampaignStatus.completed));
    }
  };

  const onPause = () => {
    const { campaignId } = bundleCampaign || {};
    if (campaignId && !isLoading) {
      setLoadingState(ECampaignActions.pause);
      dispatch(updateBundleCampaignStatus(campaignId, ECampaignStatus.paused));
    }
  };

  const onTakeLive = () => {
    const { campaignId } = bundleCampaign || {};
    if (campaignId && !isLoading) {
      setLoadingState(ECampaignActions.takeLive);
      dispatch(updateBundleCampaignStatus(campaignId, ECampaignStatus.active));
    }
  }
  const onReject = () => {
    const { campaignId } = bundleCampaign || {};
    if (campaignId && !isLoading) {
      setLoadingState(ECampaignActions.reject);
      dispatch(updateBundleCampaignStatus(campaignId, ECampaignStatus.rejected));
    }
  };


  useEffect(() => {
    if (!isGettingCampaignLoading && !bundleCampaign) {
      dispatch(setLocation("/cart-bundles"));
    }
  }, [isGettingCampaignLoading, isGettingCampaignDone, bundleCampaign, dispatch]);

  const onBackClick = () => {
    dispatch(setLocation("/cart-bundles"));
  };

  const goToCartBundleForm = () => {
    dispatch(clearCreationBundleForm())
    delete bundleCampaign?.createdBy;
    dispatch({
      type: BUNDLE_PROMOTION_SET_VALUE,
      payload: { ...normalizeBundleFormData(bundleCampaign), edit: true }
    })
    dispatch(setBundlePromotionType(bundleCampaign?.type as any));
    dispatch(setLocation("/cart-bundle-create"))
    dispatch(clearApi(BUNDLE_PROMOTION_CREATE));
  }

  const [
    isApproveAvailable,
    isRejectAvailable,
    isCompleteAvailable,
    isPauseAvailable,
    approveTitle,
    rejectTitle,
    completeTitle,
    pauseTitle,
    takeLiveTitle,
  ] = useMemo(() => {
    if (availableUserActions.length > 0) {
      const approveObj =
        availableUserActions.filter(
          (permission) => permission.action === ECampaignActions.approve
        )[0] || {};
      const rejectObj =
        availableUserActions.filter(
          (permission) => permission.action === ECampaignActions.reject
        )[0] || {};
      const completeObj =
        availableUserActions.filter(
          (permission) => permission.action === ECampaignActions.complete
        )[0] || {};
      const pauseObj =
        availableUserActions.filter(
          (permission) => permission.action === ECampaignActions.pause
        )[0] || {};
      const takeLiveObj =
        availableUserActions.filter(
          (permission) => permission.action === ECampaignActions.takeLive
        )[0] || {};
      return [
        approveObj?.action,
        rejectObj?.action,
        completeObj?.action,
        pauseObj?.action,
        approveObj?.title,
        rejectObj?.title,
        completeObj?.title,
        pauseObj?.title,
        takeLiveObj?.title,
      ];
    }
    return [];
  }, [availableUserActions]);

  if (!bundleCampaign || isGettingCampaignLoading) {
    return (
      <Layout>
        <Section>
          <FormLoader />
        </Section>
      </Layout>
    );
  }
  return (
    <Layout>
      <Section>
        <Card padding={["40px 20px", "40px 20px", "50px 100px", "50px 100px"]}>
          {status === 'rejected' || roles?.includes(ERoles.MANAGER) || (roles?.includes(ERoles.READONLY)) || status === ECampaignStatus.active ?
            <CampaignDetailsComponent formValues={bundleCampaign} error={error} />
            :
            <CampaignDetailsComponent formValues={bundleCampaign} error={error} onEditBundleInfo={goToCartBundleForm} />
          }

          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            flexDirection={["column-reverse", "column-reverse", "row", "row"]}
          >
            <Link
              data-testid='back-btn'
              variant="default"
              color="primary"
              onClick={onBackClick}
              marginRight="15px"
            >
              Back
            </Link>
            {isRejectAvailable && (
              <Button
                round
                variant="outlined"
                color="primary"
                width="auto"
                minWidth="200px"
                margin="0 15px 0 0"
                onClick={onReject}
                data-testid='reject-button'
                disabled={loadingState === ECampaignActions.reject}
              >
                {rejectTitle}
              </Button>
            )}

            {isCompleteAvailable && (
              <Button
                round
                variant="outlined"
                color="primary"
                width="auto"
                minWidth="200px"
                margin="0 15px 0 0"
                onClick={onComplete}
                data-testid='complete-button'
                disabled={loadingState === ECampaignActions.complete}
              >
                {completeTitle}
              </Button>
            )}
            {isPauseAvailable && (
              <Button
                round
                variant="outlined"
                color="primary"
                width="auto"
                minWidth="200px"
                margin="0 15px 0 0"
                onClick={onPause}
                data-testid='pause-button'
                disabled={loadingState === ECampaignActions.pause}
              >
                {pauseTitle}
              </Button>
            )}
            {takeLiveTitle && (
              <Button
                round
                variant="outlined"
                color="primary"
                width="auto"
                minWidth="200px"
                margin="0 15px 0 0"
                onClick={onTakeLive}
                data-testid='take-live-button'
                disabled={loadingState === ECampaignActions.takeLive}
              >
                {takeLiveTitle}
              </Button>
            )
            }
            {isApproveAvailable && (
              <Button
                round
                variant="contained"
                color="secondary"
                width="auto"
                minWidth="200px"
                onClick={onApprove}
                data-testid='approve-button'
                disabled={loadingState === ECampaignActions.approve}
              >
                {approveTitle}
              </Button>
            )}
          </Box>
        </Card>
      </Section>
    </Layout>
  );
};

export default CampaignDetails;
